import { useCallback, useState, ReactNode } from "react";
import { ScreeningItem } from "../../types/Screening";
import { Loading } from "../loading/Loading";
import { useTranslation } from "react-i18next";
import { TabPanel, TabView, TabViewTabChangeEvent } from "primereact/tabview";
import { ScreeningParticulars } from "./ScreeningParticulars";
import { ScreeningSummary } from "./ScreeningSummary";
import { ScreeningGradeComparison } from "./ScreeningGradeComparison";
import { ScreeningPscPerformance } from "./ScreeningPscPerformance";
import { ScreeningComplianceRisk } from "./ScreeningComplianceRisk";
import { selectScreening } from "../../features/screeningSlice";
import { useAppSelector } from "../../redux/hooks";
import "./ScreeningTabs.css";
import { ScreeningIncidents } from "./ScreeningIncidents";
import { sendGAEvent } from "../../services/AnalyticsService";
import { SCREENING } from "../../utils/analytics";
import { screeningTabActionMap } from "../../constants/GaEvents";

type Props = {
    data: ScreeningItem | undefined;
    loading: boolean;
    tabBarAction?: ReactNode;
};

export const ScreeningTabs = (props: Props) => {
    const { data, loading, tabBarAction } = props;

    const { t } = useTranslation("locale");

    const { pscInspections, pscLoading, pscError } =
        useAppSelector(selectScreening);

    const [activeIndex, setActiveIndex] = useState(0);

    const onTabChange = useCallback((event: TabViewTabChangeEvent) => {
        setActiveIndex(event.index);
        sendGAEvent(SCREENING, screeningTabActionMap[event.index]);
    }, []);

    if (loading) {
        return <Loading />;
    }

    if (!data) {
        return (
            <div
                className="flex justify-content-center align-items-center h-3rem"
                style={{ backgroundColor: "#FCFCFC" }}
            >
                {t("screening.noData")}
            </div>
        );
    }

    const getScreeningIncidentsTab = () => {
        return (
            <TabPanel header={t("tabs.incidents")}>
                <ScreeningIncidents />
            </TabPanel>
        );
    };

    const getScreeningComplianceRiskTab = () => {
        return (
            <TabPanel header={t("tabs.compliance_risk")}>
                <ScreeningComplianceRisk />
            </TabPanel>
        );
    };

    return (
        <div className="mt-6">
            <div className="mt-3 z-0" style={{ position: "relative" }}>
                {tabBarAction && (
                    <div className="screening-tab-bar-action">
                        {tabBarAction}
                    </div>
                )}
                <TabView activeIndex={activeIndex} onTabChange={onTabChange}>
                    <TabPanel header={t("tabs.summary")}>
                        <ScreeningSummary data={data} />
                    </TabPanel>
                    <TabPanel header={t("tabs.particulars")}>
                        <ScreeningParticulars data={data} />
                    </TabPanel>
                    <TabPanel header={t("tabs.grade_comparison")}>
                        <ScreeningGradeComparison
                            imo={`${data.vessel_imo}`}
                            vesselGrade={data}
                        />
                    </TabPanel>
                    {getScreeningIncidentsTab()}
                    <TabPanel header={t("tabs.psc_performance")}>
                        <ScreeningPscPerformance
                            pscInspections={pscInspections}
                            pscLoading={pscLoading}
                            pscError={pscError}
                        />
                    </TabPanel>
                    {getScreeningComplianceRiskTab()}
                </TabView>
            </div>
        </div>
    );
};
